import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { X } from 'lucide-react'
import BruktbilfinansieringInfoBox from './BruktbilfinansieringInfoBox'
import { useBorrowerAgeProfile } from './hooks/useBorrowerAgeProfile'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'
import type { BorrowerAgeProfile } from '../../types/bruktbilfinansiering'
import { ALL_SCOPE1_CLASSES, SCOPE1_CLASS_GROUPS, type Scope1Class } from '../../types/bruktbilfinansiering'
import { KNOWN_BANKS } from './knownBanks'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const MAX_COMPARE_BANKS = 5
const SMALL_SAMPLE_THRESHOLD = 10

const COMPARISON_COLORS = [
  { line: 'rgba(239, 68, 68, 1)',  fill: 'rgba(239, 68, 68, 0.05)' },
  { line: 'rgba(16, 185, 129, 1)', fill: 'rgba(16, 185, 129, 0.05)' },
  { line: 'rgba(245, 158, 11, 1)', fill: 'rgba(245, 158, 11, 0.05)' },
  { line: 'rgba(139, 92, 246, 1)', fill: 'rgba(139, 92, 246, 0.05)' },
  { line: 'rgba(236, 72, 153, 1)', fill: 'rgba(236, 72, 153, 0.05)' },
]
const SELF_COLOR = { line: 'rgba(59, 130, 246, 1)', fill: 'rgba(59, 130, 246, 0.15)' }

const fmtAge = (n: number, t: TFunction<'bbf'>) => t('shared.years', { value: n.toFixed(1) })
const fmtAgeInt = (n: number, t: TFunction<'bbf'>) => t('shared.years', { value: Math.round(n) })
const fmtNum = (n: number) => new Intl.NumberFormat('nb-NO').format(n)

function compactAge(n: number): string {
  return String(n)
}

interface Props {
  bankOrgNr: string
  bankName: string
  filters: BruktbilfinansieringFilters
}

type DisplayMode = 'count' | 'percentage'

// Compute median, p25, p75 from a sequence of (bucket_left, count) values.
function quantilesFromBuckets(profile: BorrowerAgeProfile, step: number): { median: number | null; p25: number | null; p75: number | null; avg: number | null } {
  const total = profile.total_borrowers
  if (total === 0) return { median: null, p25: null, p75: null, avg: null }
  let cum = 0
  let p25: number | null = null
  let median: number | null = null
  let p75: number | null = null
  let weightedSum = 0
  for (const pt of profile.points) {
    weightedSum += pt.count * (pt.age + step / 2)
    cum += pt.count
    if (p25 === null && cum >= total * 0.25) p25 = pt.age + step / 2
    if (median === null && cum >= total * 0.5) median = pt.age + step / 2
    if (p75 === null && cum >= total * 0.75) p75 = pt.age + step / 2
  }
  return { median, p25, p75, avg: weightedSum / total }
}

export default function AlderProfilTab({ bankOrgNr, bankName, filters }: Props) {
  const { t } = useTranslation('bbf')
  const [selectedClasses, setSelectedClasses] = useState<string[]>([...ALL_SCOPE1_CLASSES])
  const [compareBanks, setCompareBanks] = useState<{ org_nr: string; name: string }[]>([])
  const [displayMode, setDisplayMode] = useState<DisplayMode>('percentage')

  const { data, loading, error } = useBorrowerAgeProfile(
    bankOrgNr,
    filters,
    selectedClasses,
    compareBanks.map(b => b.org_nr),
  )

  function toggleClass(cls: string) {
    setSelectedClasses(prev => prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls])
  }

  function selectGroup(group: string) {
    const groupClasses = SCOPE1_CLASS_GROUPS[group] as string[]
    const allSelected = groupClasses.every(c => selectedClasses.includes(c))
    if (allSelected) setSelectedClasses(prev => prev.filter(c => !groupClasses.includes(c)))
    else setSelectedClasses(prev => [...new Set([...prev, ...groupClasses])])
  }

  const profiles = data?.profiles ?? []
  const step = profiles[0]?.points.length
    ? (profiles[0].points[1]?.age ?? 1) - (profiles[0].points[0]?.age ?? 0)
    : 1

  const chartData = useMemo(() => {
    if (profiles.length === 0) return null
    const labels = profiles[0].points.map(p => compactAge(p.age))
    const datasets = profiles.map((profile, i) => {
      const palette = profile.is_self ? SELF_COLOR : COMPARISON_COLORS[(i - 1 + COMPARISON_COLORS.length) % COMPARISON_COLORS.length]
      return {
        label: profile.bank_name,
        data: profile.points.map(p => displayMode === 'count' ? p.count : p.percentage),
        borderColor: palette.line,
        backgroundColor: profile.is_self ? palette.fill : 'transparent',
        fill: profile.is_self,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: profile.is_self ? 2.5 : 2,
      }
    })
    return { labels, datasets }
  }, [profiles, displayMode])

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { position: 'bottom' as const },
      tooltip: {
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const idx = items[0]?.dataIndex ?? 0
            const left = profiles[0]?.points[idx]?.age ?? 0
            return t('ageProfile.tooltipRange', { from: left, to: left + step })
          },
          label: (ctx: { datasetIndex: number; parsed: { y: number | null } }) => {
            const profile = profiles[ctx.datasetIndex]
            if (!profile) return ''
            const y = ctx.parsed.y ?? 0
            if (displayMode === 'count') return t('ageProfile.tooltipBorrowers', { bank: profile.bank_name, n: fmtNum(y) })
            return `${profile.bank_name}: ${y.toFixed(1)} %`
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: t('ageProfile.xAxis') }, ticks: { maxRotation: 0, autoSkip: true } },
      y: {
        title: { display: true, text: displayMode === 'count' ? t('ageProfile.borrowersCount') : t('ageProfile.percentOfBorrowers') },
        beginAtZero: true,
      },
    },
  }), [profiles, step, displayMode, t])

  const smallSamples = profiles.filter(p => p.total_borrowers > 0 && p.total_borrowers < SMALL_SAMPLE_THRESHOLD)
  const zeroSamples = profiles.filter(p => p.total_borrowers === 0)

  return (
    <div className="space-y-4">
      <BruktbilfinansieringInfoBox bankName={bankName} filters={filters} tab="alder-profil" overrideClasses={selectedClasses} />

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
        <div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-3">{t('shared.banksLabel')}</span>
          <span className="inline-flex items-center px-2 py-1 rounded-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs mr-2">
            {t('shared.you', { bank: bankName })}
          </span>
          {compareBanks.map(b => (
            <span key={b.org_nr} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-200 mr-2">
              {b.name}
              <button
                onClick={() => setCompareBanks(prev => prev.filter(x => x.org_nr !== b.org_nr))}
                className="hover:text-red-600"
                aria-label={t('shared.remove', { name: b.name })}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <CompareBankPicker
            existing={[bankOrgNr, ...compareBanks.map(b => b.org_nr)]}
            disabled={compareBanks.length >= MAX_COMPARE_BANKS}
            onAdd={b => setCompareBanks(prev => [...prev, b])}
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('shared.classesLabel')}</span>
            <button onClick={() => setSelectedClasses([...ALL_SCOPE1_CLASSES])} className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">{t('shared.all')}</button>
            <button onClick={() => setSelectedClasses([...SCOPE1_CLASS_GROUPS['BILBRANSJE'] as string[]])} className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">BILBRANSJE</button>
            <button onClick={() => setSelectedClasses([...SCOPE1_CLASS_GROUPS['PERSON'] as string[]])} className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">PERSON</button>
            <button onClick={() => setSelectedClasses([])} className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">{t('shared.clearAll')}</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(SCOPE1_CLASS_GROUPS).map(([group, classes]) => (
              <div key={group}>
                <button onClick={() => selectGroup(group)} className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 hover:underline">
                  {group}
                </button>
                {(classes as Scope1Class[]).map(cls => (
                  <label key={cls} className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={selectedClasses.includes(cls)} onChange={() => toggleClass(cls)} className="accent-primary-600" />
                    <span className="text-xs text-gray-700 dark:text-gray-300">
                      {(cls as string).replace(`${group}_TO_`, '')}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.display')}</span>
          <label className="text-sm flex items-center gap-1">
            <input type="radio" checked={displayMode === 'count'} onChange={() => setDisplayMode('count')} />
            {t('ageProfile.borrowersCount')}
          </label>
          <label className="text-sm flex items-center gap-1">
            <input type="radio" checked={displayMode === 'percentage'} onChange={() => setDisplayMode('percentage')} />
            {t('ageProfile.percentOfBorrowers')}
          </label>
        </div>
      </div>

      {smallSamples.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-2 text-sm text-yellow-800 dark:text-yellow-200">
          <strong>{t('profile.smallSample')}</strong>{' '}
          {t('profile.smallSampleBody', { list: smallSamples.map(p => `${p.bank_name} (n=${p.total_borrowers})`).join(', ') })}
        </div>
      )}
      {zeroSamples.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
          {t('ageProfile.noBorrowers', { list: zeroSamples.map(p => p.bank_name).join(', ') })}
        </div>
      )}

      {profiles.some(p => p.over_max_count > 0) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 text-sm text-blue-800 dark:text-blue-200">
          <strong>{t('ageProfile.overMax')}</strong>{' '}
          {profiles
            .filter(p => p.over_max_count > 0)
            .map(p => {
              const pct = p.total_borrowers > 0 ? (p.over_max_count * 100 / p.total_borrowers).toFixed(1) : '0.0'
              return t('ageProfile.overMaxItem', { bank: p.bank_name, n: fmtNum(p.over_max_count), pct })
            })
            .join(', ')}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">{t('shared.loading')}</p>}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {!loading && !error && chartData && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" style={{ height: 420 }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      )}

      {!loading && !error && profiles.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-400">{t('shared.bank')}</th>
                <th className="px-3 py-2 text-right">{t('ageProfile.borrowersCount')}</th>
                <th className="px-3 py-2 text-right">{t('ageProfile.over80')}</th>
                <th className="px-3 py-2 text-right">{t('ageProfile.avgAge')}</th>
                <th className="px-3 py-2 text-right">P25</th>
                <th className="px-3 py-2 text-right">Median</th>
                <th className="px-3 py-2 text-right">P75</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(profile => {
                const q = quantilesFromBuckets(profile, step)
                return (
                  <tr key={profile.bank_org_nr} className={`border-b border-gray-100 dark:border-gray-700 ${profile.is_self ? 'bg-blue-50 dark:bg-blue-900/30 font-semibold' : ''}`}>
                    <td className="px-3 py-2">{profile.bank_name}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(profile.total_borrowers)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(profile.over_max_count)}</td>
                    <td className="px-3 py-2 text-right">{q.avg !== null ? fmtAge(q.avg, t) : '–'}</td>
                    <td className="px-3 py-2 text-right">{q.p25 !== null ? fmtAgeInt(q.p25, t) : '–'}</td>
                    <td className="px-3 py-2 text-right">{q.median !== null ? fmtAgeInt(q.median, t) : '–'}</td>
                    <td className="px-3 py-2 text-right">{q.p75 !== null ? fmtAgeInt(q.p75, t) : '–'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function CompareBankPicker({ existing, disabled, onAdd }: {
  existing: string[]
  disabled: boolean
  onAdd: (b: { org_nr: string; name: string }) => void
}) {
  const { t } = useTranslation('bbf')
  const available = KNOWN_BANKS.filter(b => !existing.includes(b.org_nr))
  return (
    <select
      disabled={disabled || available.length === 0}
      value=""
      onChange={e => {
        const b = available.find(x => x.org_nr === e.target.value)
        if (b) onAdd(b)
      }}
      className="text-xs px-2 py-1 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50"
    >
      <option value="">{t('shared.addComparison')}</option>
      {available.map(b => (
        <option key={b.org_nr} value={b.org_nr}>{b.name}</option>
      ))}
    </select>
  )
}
